import { useEffect } from "react"
import { Navigate, useParams } from "react-router-dom"

import { Select } from "@/atoms"
import { NAMES_ROUTES, PARAMS } from "@/const/routes.constants"
import { isScopeSummaryReport } from "@/domain/report"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { FolderViewOutlined } from "@/icons"
import { InformationAlert } from "@/molecules/InformationAlert"
import { Form, RESOURCES, Rules } from "@/organisms/Form"
import { useReportsDefinitions } from "@/pages/ReportsPage/applications/useReports"
import { getCronExpression } from "@/pages/ReportsPage/helpers/cron.helpers"
import { AdditionalFieldsDetailsSection } from "./components/AdditionalFieldsDetailsSection"
import { ReportFrequencySectionForm } from "./components/ReportFrequencySectionForm"
import { ShareReportSectionForm } from "./components/ShareReportSectionForm"
import { getInitialValues, isOnce } from "./helpers"

import type { RecurringReportFormProps } from "."

export const RecurringReportForm = (props: RecurringReportFormProps) => {
  const { form, formId, entityId, message, isNewEntity, custom } =
    Form.useForm()
  const { listLink, actions } = props

  const keyPrefix = "reports.form"
  const { t } = useTranslation()
  const { t: tForm } = useTranslation("translation", { keyPrefix })
  const { logger, EVENTS } = useLogger()
  const params = useParams()
  const duplicateId = params[PARAMS.DUPLICATE_ID]
  const createDuplicate = !!duplicateId
  const [{ response, isLoading, isError }, { scopesCtx }] =
    useReportsDefinitions({
      needle: entityId || duplicateId
    })
  const { data = [] } = response || {}
  const { data: scopesData = [] } = scopesCtx.response || {}
  const loading = isLoading || scopesCtx.isLoading
  const frequency = Form.useWatch("frequency", form)
  const reportType = Form.useWatch("reportType", form)
  const { ROOT, WAREHOUSE } = NAMES_ROUTES.REPORTS
  const scopeSummaryReport = isScopeSummaryReport(reportType)
  const isDirty = custom?.isDirty

  useEffect(() => {
    if (!loading && !isDirty && createDuplicate)
      queueMicrotask(() => custom?.setIsDirty?.(true))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isDirty])

  if (!loading && isError && createDuplicate)
    return <Navigate replace to={listLink} />

  return (
    <Form
      id={formId}
      loading={loading}
      initialValues={getInitialValues(data, isNewEntity, createDuplicate)}
      navigateOnFinished={
        isOnce(frequency) ? `/${ROOT}/${WAREHOUSE}` : listLink
      }
      onValuesChange={(_, values) => {
        if (
          values.resources !== RESOURCES.ALL &&
          isScopeSummaryReport(values.reportType)
        )
          form.setFieldValue("resources", RESOURCES.ALL)
      }}
      onFinish={async (values) => {
        const action = isNewEntity ? "created" : "updated"
        const { name_details: name } = values || {}

        try {
          const {
            description_details,
            specificScopes,
            reportType,
            sendEmail,
            resources,
            frequency,
            time,
            unit,
            days
          } = values

          const scopes = [
            resources === RESOURCES.ALL ? scopesData[1]?.id : specificScopes
          ]

          const payload = {
            name,
            sendEmail,
            scopes,
            description: description_details,
            report_type: reportType,
            schedule: isOnce(frequency)
              ? ""
              : getCronExpression(unit, time, days)
          }

          if (isNewEntity) {
            await actions.creator.mutateAsync(payload as any)

            message.onSuccess(name, { action })
          } else {
            await actions.updater.mutateAsync({
              needle: `/${entityId}`,
              data: payload
            } as any)

            message.onSuccess(name, { action })
          }

          const { CREATE_REPORT, UPDATE_REPORT } =
            EVENTS.ANALYTIC_EVENTS.REPORTS
          const metaLogger = {
            sendEmail,
            report_type: payload.report_type,
            schedule: frequency
          }

          logger.info(isNewEntity ? CREATE_REPORT : UPDATE_REPORT, metaLogger)
        } catch (error) {
          message.onFailed(name, { action, error })

          return Promise.reject()
        }
      }}
    >
      <Form.Section.Details
        noDescription
        title={tForm("sections.details")}
        label={tForm("fields.name.label")}
      >
        <AdditionalFieldsDetailsSection />
      </Form.Section.Details>
      <Form.Section.Resources
        disabled={scopeSummaryReport}
        keyPrefix={`${keyPrefix}.sections.resources`}
        form={form}
        Icon={FolderViewOutlined}
        info={
          scopeSummaryReport && (
            <InformationAlert
              message={tForm("fields.reportType.scope.info")}
              palette="neutral"
            />
          )
        }
      >
        <Form.Item
          name="specificScopes"
          rules={[Rules.getRequiredFieldRule(t)]}
        >
          <Select
            allowClear
            fieldNames={{ label: "name", value: "id" }}
            options={scopesData[0]}
          />
        </Form.Item>
      </Form.Section.Resources>
      <ReportFrequencySectionForm form={form} t={tForm} />
      <ShareReportSectionForm />
    </Form>
  )
}
